import React, {useState} from 'react';
import {getIn} from 'formik';
import {
    Alert, Button, CircularProgress, MenuItem, Skeleton, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import '../UsersNewOrEdit.css';
import {helper} from "../../../utils/helper";
import {toastHelper} from "../../../utils/toast-helper";
import BlocCalibration from "./BlocCalibration";
import {eligibiliteAgence, PERIODE_MAX_JOURS, pointDepuisGeocodage, pointVide, toggleContraint} from "./simulationRules";

/**
 * Briques de formulaire du wizard Simulation (TOUR-03 lot 3). Mêmes conventions que les pages formulaire :
 * label `Grid size={3}` / contrôle `Grid size={9}`, toggles Oui/Non maison (`toggle-button-small`).
 */

/** Ligne de champ : label 3 / contrôle 9. */
export function FieldRow({label, required, hint, children, compact = false}) {
    // `compact` (volet de l'atelier, 360 px) : le libellé passe au-dessus du contrôle.
    if (compact) {
        return (
            <div style={{marginBottom: 12}}>
                <Typography component="div" sx={{fontSize: 12, fontWeight: 600, color: '#5A6470', mb: 0.5}}>
                    {label} {required && <span className="asterix">*</span>}
                </Typography>
                {children}
                {hint && <Typography variant="caption" sx={{display: 'block', color: 'var(--thm-gray)'}}>{hint}</Typography>}
            </div>
        );
    }
    return (
        <Grid container size={12} alignItems="center" sx={{mb: 1}}>
            <Grid size={3}>
                {label} {required && <span className="asterix">*</span>}
            </Grid>
            <Grid size={9}>
                {children}
                {hint && <Typography variant="caption" sx={{display: 'block', color: 'var(--thm-gray)'}}>{hint}</Typography>}
            </Grid>
        </Grid>
    );
}

/** Message d'erreur d'un champ : affiché dès que l'étape a été tentée. */
export function useErreur(formik, tentee) {
    return (chemin) => {
        const e = getIn(formik.errors, chemin);
        return tentee && typeof e === 'string' ? e : undefined;
    };
}

export function TextInput({formik, name, type = 'text', erreur, ...rest}) {
    const e = erreur(name);
    return (
        <TextField
            name={name}
            type={type}
            value={getIn(formik.values, name) ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(e)}
            helperText={e}
            variant="outlined"
            size="small"
            fullWidth
            {...(type === 'date' ? {InputLabelProps: {shrink: true}} : {})}
            {...rest}
        />
    );
}

/** Toggle Oui / Non maison (vert / rouge). `disabled` grise les deux boutons. */
export function OuiNon({value, onChange, disabled = false, ariaLabel}) {
    return (
        <ToggleButtonGroup exclusive size="small" value={value ? 'oui' : 'non'} disabled={disabled}
                           aria-label={ariaLabel}
                           onChange={(_e, v) => v && onChange(v === 'oui')}>
            <ToggleButton value="oui" className={value ? "toggle-button-small button-selected-green" : "toggle-button-small"}>Oui</ToggleButton>
            <ToggleButton value="non" className={!value ? "toggle-button-small button-selected-red" : "toggle-button-small"}>Non</ToggleButton>
        </ToggleButtonGroup>
    );
}

/**
 * Base de départ / dépôt : sélecteur d'agence (coordonnées de l'agence) avec repli « Adresse libre » géocodée.
 * `facultatif` ajoute l'état « aucun » (dépôt local). Une agence sans coordonnées est inéligible, avec explication (U-8).
 */
export function PointPicker({value, onChange, agences, agencesLoading, facultatif = false, erreur}) {
    const [geocodage, setGeocodage] = useState(false);
    const mode = value ? value.mode : 'aucun';

    const changerMode = (_e, m) => {
        if (!m) return;
        if (m === 'aucun') onChange(null);
        else onChange({...pointVide(), ...(value || {}), mode: m});
    };

    const geocoder = async () => {
        const adresse = (value?.adresse || '').trim();
        if (!adresse) return;
        setGeocodage(true);
        try {
            const geo = await helper.geocodeAddress(adresse);
            onChange(pointDepuisGeocodage(adresse, geo));
        } catch (e) {
            toastHelper.handleError(e, e?.message || "Échec du géocodage.");
        } finally {
            setGeocodage(false);
        }
    };

    const agence = (agences || []).find((a) => String(a.id) === String(value?.agence));
    const eligibiliteChoisie = agence ? eligibiliteAgence(agence) : null;
    const e = erreur?.();

    return (
        <Stack spacing={1}>
            <ToggleButtonGroup exclusive size="small" color="primary" value={mode} onChange={changerMode}
                               aria-label="Type de point de départ">
                <ToggleButton value="agence" className={mode === 'agence' ? "toggle-button-small button-selected-primary" : "toggle-button-small"}>Agence</ToggleButton>
                <ToggleButton value="adresse" className={mode === 'adresse' ? "toggle-button-small button-selected-primary" : "toggle-button-small"}>Adresse libre</ToggleButton>
                {facultatif &&
                    <ToggleButton value="aucun" className={mode === 'aucun' ? "toggle-button-small button-selected-primary" : "toggle-button-small"}>Aucun</ToggleButton>}
            </ToggleButtonGroup>

            {mode === 'agence' &&
                <>
                    <TextField select size="small" fullWidth label="Agence" value={value?.agence ?? ''}
                               disabled={agencesLoading}
                               error={Boolean(e)} helperText={e}
                               onChange={(ev) => onChange({...pointVide(), mode: 'agence', agence: ev.target.value})}>
                        {(agences || []).map((a) => {
                            const el = eligibiliteAgence(a);
                            return (
                                <MenuItem key={a.id} value={a.id} disabled={!el.eligible}>
                                    {a.libelle}{!el.eligible ? ' — sans coordonnées' : ''}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                    {(agences || []).some((a) => !eligibiliteAgence(a).eligible) &&
                        <Typography variant="caption" sx={{color: 'var(--thm-gray)'}}>
                            Une agence sans coordonnées ne peut pas servir de point de départ : renseignez son adresse
                            dans Administration &gt; Agences, ou utilisez une adresse libre.
                        </Typography>}
                    {eligibiliteChoisie && !eligibiliteChoisie.eligible &&
                        <Alert severity="warning">{eligibiliteChoisie.raison}</Alert>}
                    {eligibiliteChoisie?.eligible &&
                        <Typography variant="caption">Coordonnées de l'agence : {eligibiliteChoisie.lat}, {eligibiliteChoisie.lon}</Typography>}
                </>
            }

            {mode === 'adresse' &&
                <>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <TextField size="small" fullWidth label="Adresse" value={value?.adresse ?? ''}
                                   error={Boolean(e)} helperText={e}
                                   onChange={(ev) => onChange({...value, adresse: ev.target.value, libelle: ev.target.value, lat: null, lon: null})}/>
                        <Button color="primary" onClick={geocoder} disabled={geocodage || !(value?.adresse || '').trim()}
                                startIcon={geocodage ? <CircularProgress size={22}/> : null}>
                            Géocoder
                        </Button>
                    </Stack>
                    {value?.lat != null && value?.lon != null
                        ? <Typography variant="caption">Coordonnées obtenues : latitude {Number(value.lat).toFixed(5)}, longitude {Number(value.lon).toFixed(5)}</Typography>
                        : <Typography variant="caption" sx={{color: 'var(--thm-gray)'}}>Géocodez l'adresse pour obtenir ses coordonnées.</Typography>}
                </>
            }
        </Stack>
    );
}

/** Liste de dates (dates interdites) : champ date + « Ajouter », une puce texte « Retirer » par date. */
export function DateList({values, onChange, min, max, erreurDe}) {
    const [saisie, setSaisie] = useState('');
    const ajouter = () => {
        if (saisie && !values.includes(saisie)) onChange([...values, saisie].sort());
        setSaisie('');
    };
    return (
        <Stack spacing={1}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <TextField type="date" size="small" value={saisie} onChange={(e) => setSaisie(e.target.value)}
                           InputLabelProps={{shrink: true}} inputProps={{min: min || undefined, max: max || undefined}}/>
                <Button color="primary" onClick={ajouter} disabled={!saisie}>Ajouter</Button>
            </Stack>
            {values.length === 0 && <Typography variant="caption" sx={{color: 'var(--thm-gray)'}}>Aucune date interdite.</Typography>}
            {values.map((d, i) => (
                <Stack key={d} direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2">{d}</Typography>
                    <Button color="primary" size="small" onClick={() => onChange(values.filter((x) => x !== d))}>Retirer</Button>
                    {erreurDe?.(i) && <Typography variant="caption" sx={{color: 'red.main'}}>{erreurDe(i)}</Typography>}
                </Stack>
            ))}
        </Stack>
    );
}

/** Liste des PAV figés (verrous jours) : nomenclature + date, éditable (pré-remplie par « Figer ce jour »). */
export function PavList({values, onChange, min, max, erreurDe}) {
    const [nomenclature, setNomenclature] = useState('');
    const [date, setDate] = useState('');
    const ajouter = () => {
        const n = nomenclature.trim();
        if (!n || !date) return;
        if (!values.some((j) => j.nomenclature === n && j.date === date)) onChange([...values, {nomenclature: n, date}]);
        setNomenclature('');
        setDate('');
    };
    return (
        <Stack spacing={1}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <TextField size="small" label="PAV (nomenclature)" value={nomenclature} onChange={(e) => setNomenclature(e.target.value)}/>
                <TextField type="date" size="small" value={date} onChange={(e) => setDate(e.target.value)}
                           InputLabelProps={{shrink: true}} inputProps={{min: min || undefined, max: max || undefined}}/>
                <Button color="primary" onClick={ajouter} disabled={!nomenclature.trim() || !date}>Ajouter</Button>
            </Stack>
            {values.length === 0 &&
                <Typography variant="caption" sx={{color: 'var(--thm-gray)'}}>
                    Aucun PAV figé. « Figer ce jour » sur une visite d'un scénario ajoute un verrou ici.
                </Typography>}
            {values.map((j, i) => (
                <Stack key={`${j.nomenclature}@${j.date}`} direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2"><strong>{j.nomenclature}</strong> le {j.date}</Typography>
                    <Button color="primary" size="small" onClick={() => onChange(values.filter((_x, k) => k !== i))}>Retirer</Button>
                    {erreurDe?.(i) && <Typography variant="caption" sx={{color: 'red.main'}}>{erreurDe(i)}</Typography>}
                </Stack>
            ))}
        </Stack>
    );
}

/* ------------------------------------------------------------------ sections de champs (wizard V1 et atelier) */

/**
 * Un composant par section de champs, partagé par le wizard (`compact` faux : label 3 / contrôle 9) et le volet de
 * l'atelier (`compact` : label au-dessus). Aucune enveloppe de section ici : le conteneur est propre à chaque écran.
 */

export function ChampsPeriode({formik, tentee, compact = false, libelleFacultatif = false}) {
    const err = useErreur(formik, tentee);
    return (
        <>
            <FieldRow compact={compact} label="Libellé" required={!libelleFacultatif}
                      hint={libelleFacultatif ? "Vide : un libellé « Brouillon » est généré." : undefined}>
                <TextInput formik={formik} name="libelle" erreur={err} autoFocus={!compact}
                           placeholder={libelleFacultatif ? 'Facultatif pour jouer' : undefined}/>
            </FieldRow>
            <FieldRow compact={compact} label="Du" required>
                <TextInput formik={formik} name="du" type="date" erreur={err}/>
            </FieldRow>
            <FieldRow compact={compact} label="Au" required hint={`Période de ${PERIODE_MAX_JOURS} jours au plus.`}>
                <TextInput formik={formik} name="au" type="date" erreur={err}/>
            </FieldRow>
        </>
    );
}

export function ChampsEquipes({formik, tentee, compact = false}) {
    const err = useErreur(formik, tentee);
    return (
        <>
            <FieldRow compact={compact} label="Nombre d'équipes" required>
                <TextInput formik={formik} name="nbEquipes" type="number" erreur={err} inputProps={{min: 1, max: 5}} sx={{maxWidth: 160}}/>
            </FieldRow>
            <FieldRow compact={compact} label="Équipage">
                <TextField select size="small" name="equipage" value={formik.values.equipage}
                           onChange={formik.handleChange} sx={{minWidth: 200}}>
                    <MenuItem value="auto">Auto (le moins d'heures)</MenuItem>
                    <MenuItem value={2}>Binôme</MenuItem>
                    <MenuItem value={1}>Seul</MenuItem>
                </TextField>
            </FieldRow>
        </>
    );
}

export function ChampsDepart({formik, tentee, agences, agencesLoading, compact = false}) {
    const err = useErreur(formik, tentee);
    return (
        <>
            <FieldRow compact={compact} label="Base de départ" required>
                <PointPicker value={formik.values.base} onChange={(v) => formik.setFieldValue('base', v)}
                             agences={agences} agencesLoading={agencesLoading} erreur={() => err('base')}/>
            </FieldRow>
            <FieldRow compact={compact} label="Déplacement : l'équipe reste sur place"
                      hint="Oui : nuits hors base et forfait déplacement ; Non : aller-retour depuis la base chaque jour.">
                <OuiNon ariaLabel="Déplacement : l'équipe reste sur place" value={!!formik.values.deplacement}
                        onChange={(b) => formik.setFieldValue('deplacement', b)}/>
            </FieldRow>
            <FieldRow compact={compact} label="Dépôt local" hint="Facultatif : point intermédiaire des semaines passées sur place.">
                <PointPicker facultatif value={formik.values.depot} onChange={(v) => formik.setFieldValue('depot', v)}
                             agences={agences} agencesLoading={agencesLoading} erreur={() => err('depot')}/>
            </FieldRow>
        </>
    );
}

export function ChampsCollecte({formik, tentee, aCalendrier, calendrierLoading, calendrierErreur, compact = false}) {
    const err = useErreur(formik, tentee);
    const toggle = toggleContraint(aCalendrier, calendrierErreur);
    const v = formik.values;
    return (
        <>
            <FieldRow compact={compact} label="Contraint à la collecte"
                      hint={toggle.explication || "Un PAV n'est lavable que le jour de collecte et les jours de tolérance qui suivent."}>
                {calendrierLoading
                    ? <Skeleton variant="rectangular" width={120} height={25}/>
                    : <OuiNon ariaLabel="Contraint à la collecte" value={v.contraint && !toggle.disabled}
                              disabled={toggle.disabled} onChange={(b) => formik.setFieldValue('contraint', b)}/>}
            </FieldRow>
            <FieldRow compact={compact} label="Tolérance par flux (jours)" hint="Jours de lavage possibles après la collecte.">
                <Stack spacing={1}>
                    {v.tolerances.map((l, i) => (
                        <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                            <TextInput formik={formik} name={`tolerances.${i}.flux`} erreur={err} label="Flux" sx={{maxWidth: 120}} fullWidth={false}/>
                            <TextInput formik={formik} name={`tolerances.${i}.jours`} type="number" erreur={err} label="Jours"
                                       inputProps={{min: 0}} sx={{maxWidth: 110}} fullWidth={false}/>
                            <Button color="primary" size="small"
                                    onClick={() => formik.setFieldValue('tolerances', v.tolerances.filter((_x, k) => k !== i))}>
                                Retirer
                            </Button>
                        </Stack>
                    ))}
                    <div>
                        <Button color="primary" size="small"
                                onClick={() => formik.setFieldValue('tolerances', [...v.tolerances, {flux: '', jours: 0}])}>
                            Ajouter un flux
                        </Button>
                    </div>
                </Stack>
            </FieldRow>
        </>
    );
}

export function ChampsBudgets({formik, tentee, compact = false}) {
    const err = useErreur(formik, tentee);
    return (
        <>
            <FieldRow compact={compact} label="Heures par jour" required>
                <TextInput formik={formik} name="heuresParJour" type="number" erreur={err} inputProps={{step: 0.5}} sx={{maxWidth: 160}}/>
            </FieldRow>
            <FieldRow compact={compact} label="Jours par semaine" required>
                <TextInput formik={formik} name="joursParSemaine" type="number" erreur={err} inputProps={{min: 1, max: 7}} sx={{maxWidth: 160}}/>
            </FieldRow>
            <FieldRow compact={compact} label="Horizon imposé (journées)" hint="Vide : calculé par le simulateur.">
                <TextInput formik={formik} name="horizonJournees" type="number" erreur={err} inputProps={{min: 1}} sx={{maxWidth: 160}}/>
            </FieldRow>
            <FieldRow compact={compact} label="Horizon verrouillé"
                      hint="Oui : le solveur s'arrête à l'horizon et liste les PAV non affectés ; Non : il ajoute des journées et signale le dépassement.">
                <OuiNon value={formik.values.horizonVerrouille === true} ariaLabel="Horizon verrouillé"
                        onChange={(v) => formik.setFieldValue('horizonVerrouille', v)}/>
            </FieldRow>
        </>
    );
}

export function ChampsCouts({formik, tentee, compact = false}) {
    const err = useErreur(formik, tentee);
    return (
        <>
            <FieldRow compact={compact} label="Coût horaire (€/h)" hint="Vide : l'API fige la moyenne RHData de l'exploitant (pré-rempli par l'API depuis RHData si vide).">
                <TextInput formik={formik} name="coutHoraire" type="number" erreur={err} inputProps={{min: 0, step: 0.1}} sx={{maxWidth: 160}}/>
            </FieldRow>
            <FieldRow compact={compact} label="Forfait déplacement (€)" hint="Par personne et par semaine d'au moins 3 nuits hors base. Vide : moyenne RHData.">
                <TextInput formik={formik} name="forfaitDeplacement" type="number" erreur={err} inputProps={{min: 0, step: 1}} sx={{maxWidth: 160}}/>
            </FieldRow>
            <FieldRow compact={compact} label="Coût au km (€/km)" required hint="Obligatoire, jamais pré-rempli.">
                <TextInput formik={formik} name="coutKm" type="number" erreur={err} inputProps={{min: 0, step: 0.01}} sx={{maxWidth: 160}}/>
            </FieldRow>
        </>
    );
}

export function ChampsVerrous({formik, tentee, compact = false}) {
    const err = useErreur(formik, tentee);
    const v = formik.values;
    return (
        <>
            <FieldRow compact={compact} label="Dates interdites" hint="Aucune visite ces jours-là.">
                <DateList values={v.interdits} min={v.du} max={v.au}
                          onChange={(d) => formik.setFieldValue('interdits', d)}
                          erreurDe={(i) => err(`interdits.${i}`)}/>
            </FieldRow>
            <FieldRow compact={compact} label="PAV figés" hint="Un PAV figé est visité à la date indiquée, quoi qu'il arrive au recalcul.">
                <PavList values={v.jours} min={v.du} max={v.au}
                         onChange={(j) => formik.setFieldValue('jours', j)}
                         erreurDe={(i) => err(`jours.${i}.nomenclature`) || err(`jours.${i}.date`) || err(`jours.${i}`)}/>
            </FieldRow>
        </>
    );
}

/** `patrimoine` : identifiant du patrimoine simulé ; il active le bouton « Calibrer depuis l'historique ». */
export function ChampsAvances({formik, tentee, patrimoine = null, compact = false}) {
    const err = useErreur(formik, tentee);
    const champ = (label, name, extra = {}, hint) => (
        <FieldRow compact={compact} label={label} hint={hint}>
            <TextInput formik={formik} name={name} erreur={err} sx={{maxWidth: 160}} {...extra}/>
        </FieldRow>
    );
    return (
        <>
            {patrimoine != null && <BlocCalibration formik={formik} patrimoine={patrimoine}/>}
            {champ('Mise en place (min)', 'miseEnPlaceMin', {type: 'number'})}
            {champ('t_bac (min / bac)', 'tBacMin', {type: 'number', inputProps: {step: 0.1}})}
            {champ('k seul', 'kSeul', {type: 'number', inputProps: {step: 0.1}}, "Multiplicateur du temps d'un opérateur seul.")}
            {champ('Vitesse entre PAV (km/h)', 'vitesseKmH', {type: 'number'})}
            {champ('Vitesse des trajets base (km/h)', 'vitesseTrajetKmH', {type: 'number'})}
            {champ('Seuil bac/h', 'seuilBacsParHeure', {type: 'number'}, 'Indicateur vert à partir de ce seuil.')}
            {champ('Heure de début', 'heureDebutJournee')}
        </>
    );
}
